/**
 * CONSUMPTION renderer for a block (the 6 types). Controlled component: receives
 * the authoring `def` and emits the student's answer via `onChange`. When
 * `graded` is present (review mode), it shows the grading and the solution.
 */
import { type Component, createMemo, For, Match, Show, Switch } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { GradeResult } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
// MEDIA-F3F: `video` block consumer (VOD)
import { VideoPlayerBlock } from "@/features/media/components/video-block";

export interface BlockConsumerProps {
  kind: string;
  def: Record<string, unknown>;
  value: unknown;
  onChange: (response: unknown) => void;
  graded?: GradeResult;
}

const optionBase =
  "w-full rounded-xl border-2 px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * The parent has already matched `kind`, so each branch dispatches the validated
 * authoring `def` to its matching typed consumer. `def` is opaque authoring
 * JSON, so this is a validated trust-boundary cast.
 */
function dispatchDef(def: Record<string, unknown>): never {
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- validated def dispatched to its matching typed consumer
  return def as never;
}

/**
 * Reads a persisted student response. Responses are stored as opaque JSON, so
 * this is the single validated trust-boundary read of a block's answer shape.
 */
function asResponse<T>(value: unknown): Partial<T> {
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary read of opaque stored response JSON
  return (value ?? {}) as Partial<T>;
}

export const BlockConsumer: Component<BlockConsumerProps> = (props) => {
  const { t } = useI18n();
  const def = () => props.def;
  const locked = () => props.graded !== undefined;

  return (
    <Switch
      fallback={<p class="text-sm text-muted-foreground">{t(msg`Unsupported block type.`)}</p>}
    >
      {/* ── Quiz ──────────────────────────────────────────────────────── */}
      <Match when={props.kind === "quiz"}>
        <QuizConsumer
          def={dispatchDef(def())}
          value={props.value}
          onChange={props.onChange}
          locked={locked()}
        />
      </Match>

      {/* ── Flashcard (autoevaluado) ──────────────────────────────────── */}
      <Match when={props.kind === "flashcard"}>
        <FlashcardConsumer
          def={dispatchDef(def())}
          value={props.value}
          onChange={props.onChange}
          locked={locked()}
        />
      </Match>

      {/* ── Completar huecos ──────────────────────────────────────────── */}
      <Match when={props.kind === "fill-blank"}>
        <FillBlankConsumer
          def={dispatchDef(def())}
          value={props.value}
          onChange={props.onChange}
          locked={locked()}
        />
      </Match>

      {/* ── Emparejar ─────────────────────────────────────────────────── */}
      <Match when={props.kind === "match"}>
        <MatchConsumer
          def={dispatchDef(def())}
          value={props.value}
          onChange={props.onChange}
          locked={locked()}
        />
      </Match>

      {/* ── Code ─────────────────────────────────────────────────────────────── */}
      <Match when={props.kind === "code"}>
        <CodeConsumer
          def={dispatchDef(def())}
          value={props.value}
          onChange={props.onChange}
          locked={locked()}
        />
      </Match>

      {/* ── Escucha y escribe ─────────────────────────────────────────── */}
      <Match when={props.kind === "listen-type"}>
        <ListenTypeConsumer
          def={dispatchDef(def())}
          value={props.value}
          onChange={props.onChange}
          locked={locked()}
        />
      </Match>

      {/* ── MEDIA-F3F: Video (VOD) ──────────────────────────────────────────── */}
      <Match when={props.kind === "video"}>
        <VideoPlayerBlock
          def={dispatchDef(def())}
          value={props.value}
          onChange={props.onChange}
          locked={locked()}
        />
      </Match>
      {/* ── end MEDIA-F3F ─────────────────────────────────────────────── */}
    </Switch>
  );
};

// ─── Quiz ──────────────────────────────────────────────────────────────────

interface QuizDefLite {
  prompt: string;
  options: { id: string; text: string }[];
  correct: string[];
  multiple?: boolean;
}

const QuizConsumer: Component<{
  def: QuizDefLite;
  value: unknown;
  onChange: (r: unknown) => void;
  locked: boolean;
}> = (props) => {
  const selected = createMemo<string[]>(
    () => asResponse<{ selected: string[] }>(props.value).selected ?? [],
  );
  const toggle = (id: string) => {
    if (props.locked) {
      return;
    }
    if (props.def.multiple) {
      const cur = new Set(selected());
      cur.has(id) ? cur.delete(id) : cur.add(id);
      props.onChange({ selected: [...cur] });
    } else {
      props.onChange({ selected: [id] });
    }
  };
  const correctSet = () => new Set(props.def.correct);
  return (
    <div class="space-y-4">
      <p class="text-lg font-medium">{props.def.prompt}</p>
      <div class="space-y-2">
        <For each={props.def.options}>
          {(opt) => {
            const isSel = () => selected().includes(opt.id);
            const isCorrect = () => correctSet().has(opt.id);
            const cls = () => {
              if (props.locked) {
                if (isCorrect()) {
                  return "border-success bg-success/10";
                }
                if (isSel()) {
                  return "border-danger bg-danger/10";
                }
                return "border-border";
              }
              return isSel()
                ? "border-primary bg-primary/10"
                : "border-border hover:border-primary/50";
            };
            return (
              <button
                type="button"
                data-testid={`quiz-option-${opt.id}`}
                aria-pressed={isSel()}
                disabled={props.locked}
                onClick={() => toggle(opt.id)}
                class={`${optionBase} ${cls()}`}
              >
                {opt.text}
              </button>
            );
          }}
        </For>
      </div>
    </div>
  );
};

// ─── Flashcard ─────────────────────────────────────────────────────────────

const FlashcardConsumer: Component<{
  def: { front: string; back: string };
  value: unknown;
  onChange: (r: unknown) => void;
  locked: boolean;
}> = (props) => {
  const { t } = useI18n();
  const revealed = () => props.value !== undefined || props.locked;
  return (
    <div class="space-y-4">
      <div class="rounded-2xl border-2 border-border p-8 text-center">
        <p class="text-xl font-semibold">{props.def.front}</p>
        <Show when={revealed()}>
          <p class="mt-4 border-t border-border pt-4 text-lg text-muted-foreground">
            {props.def.back}
          </p>
        </Show>
      </div>
      <Show
        when={revealed()}
        fallback={
          <button
            type="button"
            data-testid="flashcard-reveal"
            class={`${optionBase} border-primary/50 text-center font-medium`}
            onClick={() => props.onChange({ known: false })}
          >
            {t(msg`Show answer`)}
          </button>
        }
      >
        <Show when={!props.locked}>
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="flashcard-unknown"
              class={`${optionBase} border-danger/50 text-center`}
              onClick={() => props.onChange({ known: false })}
              aria-pressed={asResponse<{ known: boolean }>(props.value).known === false}
            >
              {t(msg`I didn't know it`)}
            </button>
            <button
              type="button"
              data-testid="flashcard-known"
              class={`${optionBase} border-success/50 text-center`}
              onClick={() => props.onChange({ known: true })}
              aria-pressed={asResponse<{ known: boolean }>(props.value).known === true}
            >
              {t(msg`I knew it`)}
            </button>
          </div>
        </Show>
      </Show>
    </div>
  );
};

// ─── Fill-blank ────────────────────────────────────────────────────────────

const FillBlankConsumer: Component<{
  def: { text: string; blanks: { id: string; answers: string[] }[] };
  value: unknown;
  onChange: (r: unknown) => void;
  locked: boolean;
}> = (props) => {
  const { t } = useI18n();
  const filled = () => asResponse<{ filled: Record<string, string> }>(props.value).filled ?? {};
  const set = (id: string, text: string) => {
    props.onChange({ filled: { ...filled(), [id]: text } });
  };
  return (
    <div class="space-y-4">
      <p class="whitespace-pre-wrap text-lg">{props.def.text}</p>
      <div class="space-y-2">
        <For each={props.def.blanks}>
          {(blank, i) => (
            <div class="flex items-center gap-2">
              <span class="text-sm text-muted-foreground">#{i() + 1}</span>
              <input
                type="text"
                data-testid={`fill-blank-${blank.id}`}
                disabled={props.locked}
                value={filled()[blank.id] ?? ""}
                onInput={(e) => set(blank.id, e.currentTarget.value)}
                class="flex-1 rounded-lg border-2 border-border bg-field-background px-3 py-2 focus-visible:border-primary focus-visible:outline-none"
                placeholder={t(msg`Your answer`)}
              />
              <Show when={props.locked}>
                <span class="text-sm text-muted-foreground">→ {blank.answers[0]}</span>
              </Show>
            </div>
          )}
        </For>
      </div>
    </div>
  );
};

// ─── Match ─────────────────────────────────────────────────────────────────

const MatchConsumer: Component<{
  def: { pairs: { left: string; right: string }[] };
  value: unknown;
  onChange: (r: unknown) => void;
  locked: boolean;
}> = (props) => {
  const rights = createMemo(() =>
    props.def.pairs.map((p) => p.right).toSorted((a, b) => a.localeCompare(b)),
  );
  const chosen = () =>
    asResponse<{ matches: { left: string; right: string }[] }>(props.value).matches ?? [];
  const rightFor = (left: string) => chosen().find((m) => m.left === left)?.right ?? "";
  const set = (left: string, right: string) => {
    const others = chosen().filter((m) => m.left !== left);
    props.onChange({ matches: right ? [...others, { left, right }] : others });
  };
  return (
    <div class="space-y-2">
      <For each={props.def.pairs}>
        {(pair) => (
          <div class="flex items-center gap-3">
            <span class="w-1/2 rounded-lg border-2 border-border px-3 py-2">{pair.left}</span>
            <select
              data-testid={`match-select-${pair.left}`}
              disabled={props.locked}
              value={rightFor(pair.left)}
              onChange={(e) => set(pair.left, e.currentTarget.value)}
              class="w-1/2 rounded-lg border-2 border-border bg-field-background px-3 py-2 focus-visible:border-primary focus-visible:outline-none"
            >
              <option value="">—</option>
              <For each={rights()}>{(r) => <option value={r}>{r}</option>}</For>
            </select>
          </div>
        )}
      </For>
    </div>
  );
};

// ─── Code ──────────────────────────────────────────────────────────────────

const CodeConsumer: Component<{
  def: { prompt: string; language: string; starter?: string };
  value: unknown;
  onChange: (r: unknown) => void;
  locked: boolean;
}> = (props) => {
  const code = () => asResponse<{ code: string }>(props.value).code ?? props.def.starter ?? "";
  return (
    <div class="space-y-3">
      <p class="text-lg font-medium">{props.def.prompt}</p>
      <textarea
        data-testid="code-input"
        disabled={props.locked}
        value={code()}
        spellcheck={false}
        onInput={(e) => props.onChange({ code: e.currentTarget.value })}
        rows={8}
        class="w-full rounded-lg border-2 border-border bg-field-background p-3 font-mono text-sm focus-visible:border-primary focus-visible:outline-none"
        placeholder={`// ${props.def.language}`}
      />
    </div>
  );
};

// ─── Listen-type ───────────────────────────────────────────────────────────

const ListenTypeConsumer: Component<{
  def: { audioRef: string; transcript: string };
  value: unknown;
  onChange: (r: unknown) => void;
  locked: boolean;
}> = (props) => {
  const { t } = useI18n();
  const text = () => asResponse<{ text: string }>(props.value).text ?? "";
  const isUrl = () => /^https?:\/\//.test(props.def.audioRef);
  return (
    <div class="space-y-3">
      <Show
        when={isUrl()}
        fallback={
          <p class="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
            🔊 {props.def.audioRef}
          </p>
        }
      >
        {/* oxlint-disable-next-line jsx-a11y/media-has-caption */}
        <audio controls src={props.def.audioRef} data-testid="listen-audio" class="w-full" />
      </Show>
      <input
        type="text"
        data-testid="listen-input"
        disabled={props.locked}
        value={text()}
        onInput={(e) => props.onChange({ text: e.currentTarget.value })}
        class="w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 focus-visible:border-primary focus-visible:outline-none"
        placeholder={t(msg`Type what you hear`)}
      />
      <Show when={props.locked}>
        <p class="text-sm text-muted-foreground">
          {t(msg`Transcript`)}: {props.def.transcript}
        </p>
      </Show>
    </div>
  );
};
